import { UserData } from '../types/user';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/localStorageUtils';
import { PUBLIC_USER_FIELDS } from '../constants/userFields';

const OFFLINE_AUTH_KEY = 'offline_auth_state';
const AUTH_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export interface OfflineAuthData {
  isAuthenticated: boolean;
  userId: string | null;
  userData: UserData | null;
  timestamp: number;
}

// Only fields offline screens use. Private profile fields (email, date of
// birth, gender, country, device tokens) and anything 2FA-related stay out
// of browser storage.
const OFFLINE_USER_FIELDS: readonly string[] = ['uid', 'name', 'surname', ...PUBLIC_USER_FIELDS];

const toOfflineUserData = (userData: UserData | null): UserData | null => {
  if (!userData) return null;
  const source = userData as unknown as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  for (const field of OFFLINE_USER_FIELDS) {
    if (source[field] !== undefined) picked[field] = source[field];
  }
  return picked as unknown as UserData;
};

class OfflineAuthService {
  /**
   * Cache authentication state for offline use
   */
  cacheAuthState(userId: string, userData: UserData): void {
    try {
      const authData: OfflineAuthData = {
        isAuthenticated: true,
        userId,
        userData: toOfflineUserData(userData),
        timestamp: Date.now(),
      };
      
      safeSetItem(OFFLINE_AUTH_KEY, JSON.stringify(authData));
      console.log('[OFFLINE_AUTH] Authentication state cached for offline use');
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to cache auth state:', error);
    }
  }

  /**
   * Get cached authentication state
   */
  getCachedAuthState(): OfflineAuthData | null {
    try {
      const cached = safeGetItem(OFFLINE_AUTH_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      // Entries written by older versions held the whole profile.
      const authData: OfflineAuthData = {
        isAuthenticated: parsed.isAuthenticated === true,
        userId: typeof parsed.userId === 'string' ? parsed.userId : null,
        userData: toOfflineUserData(parsed.userData ?? null),
        timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : 0,
      };
      if ('email' in parsed || JSON.stringify(parsed.userData ?? null) !== JSON.stringify(authData.userData)) {
        safeSetItem(OFFLINE_AUTH_KEY, JSON.stringify(authData));
      }
      
      // Check if cache is expired
      const age = Date.now() - authData.timestamp;
      if (age > AUTH_CACHE_DURATION) {
        console.log('[OFFLINE_AUTH] Cached auth state expired, clearing cache');
        this.clearAuthState();
        return null;
      }

      // console.log('[OFFLINE_AUTH] Using cached auth state (offline mode)');
      return authData;
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to get cached auth state:', error);
      return null;
    }
  }

  /**
   * Clear cached authentication state
   */
  clearAuthState(): void {
    try {
      safeRemoveItem(OFFLINE_AUTH_KEY);
      console.log('[OFFLINE_AUTH] Cached auth state cleared');
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to clear cached auth state:', error);
    }
  }

  /**
   * Check if we should use offline auth (when navigator is offline)
   */
  shouldUseOfflineAuth(): boolean {
    return !navigator.onLine;
  }

  /**
   * Update cached user data (for profile updates)
   */
  updateCachedUserData(userData: UserData): void {
    try {
      const cached = this.getCachedAuthState();
      if (cached) {
        cached.userData = toOfflineUserData(userData);
        cached.timestamp = Date.now(); // Update timestamp
        localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(cached));
        console.log('[OFFLINE_AUTH] Cached user data updated');
      }
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to update cached user data:', error);
    }
  }

  /**
   * Check if cached auth state exists and is valid
   */
  hasCachedAuth(): boolean {
    const cached = this.getCachedAuthState();
    return cached !== null && cached.isAuthenticated;
  }

  /**
   * Get cache expiry info for debugging
   */
  getCacheInfo(): { exists: boolean; age: number; expiresIn: number } | null {
    try {
      const cached = safeGetItem(OFFLINE_AUTH_KEY);
      if (!cached) return { exists: false, age: 0, expiresIn: 0 };

      const authData: OfflineAuthData = JSON.parse(cached);
      const age = Date.now() - authData.timestamp;
      const expiresIn = AUTH_CACHE_DURATION - age;

      return {
        exists: true,
        age,
        expiresIn: Math.max(0, expiresIn),
      };
    } catch (error) {
      console.error('[OFFLINE_AUTH] Failed to get cache info:', error);
      return null;
    }
  }
}

// Export singleton instance
export const offlineAuthService = new OfflineAuthService(); 